import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Building2, ClipboardList, Library, FileSearch,
  FileText, Hammer, Users, Settings, Shield, LogOut, PlugZap, GitCompare,
} from "lucide-react";
import { ROLE_LABELS } from "@/lib/constants";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "assessor", "client_viewer"] },
  { to: "/clients", label: "Clients", icon: Building2, roles: ["super_admin", "assessor", "client_viewer"] },
  { to: "/assessments", label: "Assessments", icon: ClipboardList, roles: ["super_admin", "assessor", "client_viewer"] },
  { to: "/framework", label: "Framework Library", icon: Library, roles: ["super_admin", "assessor", "client_viewer"] },
  { to: "/compliance-matrix", label: "Compliance Matrix", icon: GitCompare, roles: ["super_admin", "assessor", "client_viewer"] },
  { to: "/evidence", label: "Evidence", icon: FileSearch, roles: ["super_admin", "assessor", "client_viewer"] },
  { to: "/integrations", label: "Integrations", icon: PlugZap, roles: ["super_admin", "assessor"] },
  { to: "/reports", label: "Reports", icon: FileText, roles: ["super_admin", "assessor", "client_viewer"] },
  { to: "/remediation", label: "Remediation Roadmap", icon: Hammer, roles: ["super_admin", "assessor", "client_viewer"] },
  { to: "/users", label: "Users", icon: Users, roles: ["super_admin"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["super_admin"] },
];

export default function Sidebar() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const items = NAV_ITEMS.filter((i) => i.roles.includes(user.role));

  return (
    <aside
      className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-zinc-800 flex flex-col z-40"
      data-testid="sidebar"
    >
      <div className="px-6 py-5 border-b border-zinc-800 flex items-center gap-3">
        <div className="w-8 h-8 rounded-md border border-zinc-700 bg-[#121212] flex items-center justify-center">
          <Shield className="w-4 h-4 text-white" aria-hidden />
        </div>
        <div>
          <div className="text-white text-sm font-medium tracking-tight">CyberAssess</div>
          <div className="label-uppercase mt-0.5">Multi-Framework GRC</div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {items.map((item) => {
          const Icon = item.icon;
          const active = pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`sidebar-link-${item.to.replace("/", "")}`}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                active
                  ? "bg-zinc-900 text-white border border-zinc-800"
                  : "text-zinc-400 hover:bg-zinc-900/50 hover:text-white border border-transparent"
              }`}
            >
              <Icon className="w-4 h-4" aria-hidden />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-zinc-800 p-3">
        <div className="px-2 py-2 rounded-md bg-[#121212] border border-zinc-800">
          <div className="text-xs text-white font-medium truncate" data-testid="user-name">{user.name}</div>
          <div className="text-[10px] text-zinc-500 truncate">{user.email}</div>
          <div className="label-uppercase mt-1 text-zinc-400">{ROLE_LABELS[user.role]}</div>
        </div>
        <button
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          data-testid="logout-button"
          className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" aria-hidden /> Sign out
        </button>
      </div>
    </aside>
  );
}
